import React from 'react';
import T from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';

import { getMainClientInfo } from '@dmi/shared/redux/Main/selectors';
import { setIsSecondAccountFormEnabled } from '@dmi/shared/redux/Payments/FlexDraft/actions';
import { FLEX_DRAFT_INFO_COPY_DICTIONARY } from '@dmi/shared/redux/Payments/FlexDraft/constants';
import makeSelectFlexDraft, {
  selectShouldRenderValidateButton,
  selectSourceFormState,
} from '@dmi/shared/redux/Payments/FlexDraft/selectors';
import {
  clearNewInfo,
  resetFormState,
  setActiveAccountForm,
  setInput,
  setInputError,
  setIsSourceDrawerOpen,
  setUseExisting,
  validateRoutingFlexDraft,
  validateRoutingSuccess as validateRoutingSuccessAction,
} from '@dmi/shared/redux/Payments/Sources/actions';
import makeSelectPaymentSources, {
  makeSelectAccountInfoExistingByPaymentType,
  makeSelectPaymentSourcesNotifications,
  makeSelectUseAccountInfoExistingByPaymentType,
} from '@dmi/shared/redux/Payments/Sources/selectors';

import EditSourcesComponent from '../../../components/Payments/FlexDraft/EditSources';

const EditSources = ({
  accountInfoExisting,
  accountInfoNew,
  bannerError,
  customerServicePhoneNumber,
  dispatchClearNewInfo,
  dispatchResetFormState,
  dispatchSetActiveAccountForm,
  dispatchSetInput,
  dispatchSetInputError,
  dispatchSetIsSecondAccountFormEnabled,
  dispatchSetUseExisting,
  dispatchValidateRouting,
  dispatchValidateRoutingSuccess,
  formState,
  isSecondAccountFormEnabled,
  navigation,
  notifications,
  shouldRenderValidateButton,
  useExistingInfo,
  useNewInfo,
  validateRoutingError,
  validateRoutingLoading,
  validateRoutingSuccess,
}) => {
  const editSourcesProps = {
    accountInfoExisting,
    accountInfoNew,
    bannerError,
    customerServicePhoneNumber,
    dispatchClearNewInfo,
    dispatchResetFormState,
    dispatchSetActiveAccountForm,
    dispatchSetInput,
    dispatchSetInputError,
    dispatchSetIsSecondAccountFormEnabled,
    dispatchSetUseExisting,
    dispatchValidateRouting,
    dispatchValidateRoutingSuccess,
    formState,
    infoSectionData: FLEX_DRAFT_INFO_COPY_DICTIONARY.payFrom,
    isSecondAccountFormEnabled,
    navigation,
    notifications,
    shouldRenderValidateButton,
    useExistingInfo,
    useNewInfo,
    validateRoutingError,
    validateRoutingLoading,
    validateRoutingSuccess,
  };

  return (
    <EditSourcesComponent {...editSourcesProps} />
  );
};

EditSources.propTypes = {
  accountInfoExisting: T.object.isRequired,
  accountInfoNew: T.object.isRequired,
  bannerError: T.oneOfType([T.bool, T.string]).isRequired,
  customerServicePhoneNumber: T.string.isRequired,
  dispatchClearNewInfo: T.func.isRequired,
  dispatchResetFormState: T.func.isRequired,
  dispatchSetActiveAccountForm: T.func.isRequired,
  dispatchSetInput: T.func.isRequired,
  dispatchSetInputError: T.func.isRequired,
  dispatchSetIsSecondAccountFormEnabled: T.func.isRequired,
  dispatchSetUseExisting: T.func.isRequired,
  dispatchValidateRouting: T.func.isRequired,
  dispatchValidateRoutingSuccess: T.func.isRequired,
  formState: T.object.isRequired,
  isSecondAccountFormEnabled: T.bool.isRequired,
  navigation: T.object.isRequired,
  notifications: T.array.isRequired,
  shouldRenderValidateButton: T.bool.isRequired,
  useExistingInfo: T.object.isRequired,
  useNewInfo: T.object.isRequired,
  validateRoutingError: T.oneOfType([T.bool, T.string]).isRequired,
  validateRoutingLoading: T.bool.isRequired,
  validateRoutingSuccess: T.bool.isRequired,
};

const mapStateToProps = createStructuredSelector({
  /**
   * Store: Flex Draft
   */
  formState: selectSourceFormState(),
  isSecondAccountFormEnabled: makeSelectFlexDraft('isSecondAccountFormEnabled'),
  shouldRenderValidateButton: selectShouldRenderValidateButton(),
  /**
   * Store: Main
   */
  // eslint-disable-next-line sort-keys
  customerServicePhoneNumber: getMainClientInfo('customerServicePhoneNumber'),
  /**
   * Store: Payment Sources
   */
  // eslint-disable-next-line sort-keys
  accountInfoExisting: makeSelectAccountInfoExistingByPaymentType('flexdraft'),
  accountInfoNew: makeSelectPaymentSources('accountInfoNew'),
  bannerError: makeSelectPaymentSources('bannerError'),
  isSourceDrawerOpen: makeSelectPaymentSources('isSourceDrawerOpen'),
  notifications: makeSelectPaymentSourcesNotifications(),
  useExistingInfo: makeSelectUseAccountInfoExistingByPaymentType('flexdraft'),
  useNewInfo: makeSelectPaymentSources('useAccountInfoNew'),
  validateRoutingError: makeSelectPaymentSources('validateRoutingError'),
  validateRoutingLoading: makeSelectPaymentSources('validateRoutingLoading'),
  validateRoutingSuccess: makeSelectPaymentSources('validateRoutingSuccess'),
});

const mapDispatchToProps = (dispatch) => ({
  /**
   * Store: Flex Draft
   */
  dispatchSetIsSecondAccountFormEnabled:
    (payload) => dispatch(setIsSecondAccountFormEnabled(payload)),
  /**
   * Store: Payment Sources
   */
  // eslint-disable-next-line sort-keys
  dispatchClearNewInfo: () => dispatch(clearNewInfo()),
  dispatchResetFormState: (payload) => dispatch(resetFormState(payload)),
  dispatchSetActiveAccountForm: (payload) => dispatch(setActiveAccountForm(payload)),
  dispatchSetInput: (payload) => dispatch(setInput(payload)),
  dispatchSetInputError: (payload) => dispatch(setInputError(payload)),
  dispatchSetIsSourceDrawerOpen: (payload) => dispatch(setIsSourceDrawerOpen(payload)),
  dispatchSetUseExisting: (payload) => dispatch(setUseExisting(payload, 'flexdraft')),
  dispatchValidateRouting: (payload) => dispatch(validateRoutingFlexDraft(payload)),
  dispatchValidateRoutingSuccess: (payload) => dispatch(validateRoutingSuccessAction(payload)),
});

const withConnect = connect(mapStateToProps, mapDispatchToProps);

export default compose(withConnect)(EditSources);
